import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Sidebar } from '../components/Sidebar';
import { getAdminToken, clearAdminToken } from '../utils/auth';
import api, { getApiBaseUrl } from '../utils/apiClient';
import { createAppPromo, updateAppPromo, deleteAppPromo } from '../api/appPromosApi';

const fetcher = (url) => api.get(url).then((res) => res.data);

function absoluteImageUrl(imagePath) {
  if (!imagePath) return '';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const base = getApiBaseUrl();
  const p = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${base}${p}`;
}

export default function PromosPage() {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? getAdminToken() : null;
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const { data, error, isLoading, mutate } = useSWR(
    token ? '/api/app-promos' : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  const promos = data?.data ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    title: '',
    link: '',
    image: null,
    isActive: true,
    order: 0,
    screen: '',
  });

  const createPreviewUrl = useMemo(
    () => (form.image ? URL.createObjectURL(form.image) : ''),
    [form.image]
  );
  useEffect(() => {
    return () => {
      if (createPreviewUrl) URL.revokeObjectURL(createPreviewUrl);
    };
  }, [createPreviewUrl]);

  const [editForm, setEditForm] = useState({
    title: '',
    link: '',
    image: null,
    imageUrl: '',
    isActive: true,
    order: 0,
    screen: '',
  });

  const editPreviewUrl = useMemo(
    () => (editForm.image ? URL.createObjectURL(editForm.image) : ''),
    [editForm.image]
  );
  useEffect(() => {
    return () => {
      if (editPreviewUrl) URL.revokeObjectURL(editPreviewUrl);
    };
  }, [editPreviewUrl]);

  useEffect(() => {
    if (!token) navigate('/login', { replace: true });
  }, [token, navigate]);

  useEffect(() => {
    if (data?.success === false && data?.message?.toLowerCase?.().includes('token')) {
      clearAdminToken();
      navigate('/login', { replace: true });
    }
  }, [data, navigate]);

  const reload = () => mutate();

  const onPickImage = (e, isEdit) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller.');
      return;
    }
    if (isEdit) {
      setEditForm((p) => ({ ...p, image: file, imageUrl: URL.createObjectURL(file) }));
    } else {
      setForm((p) => ({ ...p, image: file }));
    }
  };

  const handleCreate = async () => {
    const title = form.title.trim();
    const link = form.link.trim();
    if (!title || !link || !form.image) {
      toast.error('Title, link, and image are required.');
      return;
    }
    const fd = new FormData();
    fd.append('title', title);
    fd.append('link', link);
    fd.append('image', form.image);
    fd.append('isActive', String(form.isActive));
    fd.append('order', String(Number(form.order) || 0));
    fd.append('screen', form.screen.trim());
    setBusy(true);
    try {
      await createAppPromo(fd);
      toast.success('Promo created.');
      setCreateOpen(false);
      setForm({
        title: '',
        link: '',
        image: null,
        isActive: true,
        order: 0,
        screen: '',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (promo) => {
    setSelected(promo);
    setEditForm({
      title: promo.title || '',
      link: promo.link || '',
      image: null,
      imageUrl: promo.image || '',
      isActive: !!promo.isActive,
      order: Number(promo.order) || 0,
      screen: promo.screen || '',
    });
    setEditOpen(true);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  const handleUpdate = async () => {
    if (!selected) return;
    const title = editForm.title.trim();
    const link = editForm.link.trim();
    if (!title || !link) {
      toast.error('Title and link are required.');
      return;
    }
    if (!editForm.image && !editForm.imageUrl) {
      toast.error('Image is required (keep existing or upload a new one).');
      return;
    }
    const fd = new FormData();
    fd.append('title', title);
    fd.append('link', link);
    fd.append('isActive', String(editForm.isActive));
    fd.append('order', String(Number(editForm.order) || 0));
    fd.append('screen', editForm.screen.trim());
    if (editForm.image) fd.append('image', editForm.image);
    setBusy(true);
    try {
      await updateAppPromo(selected.id, fd);
      toast.success('Promo updated.');
      setEditOpen(false);
      setSelected(null);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await deleteAppPromo(selected.id);
      toast.success('Promo deleted.');
      setDeleteOpen(false);
      setSelected(null);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (promo) => {
    const fd = new FormData();
    fd.append('isActive', String(!promo.isActive));
    try {
      await updateAppPromo(promo.id, fd);
      toast.success(promo.isActive ? 'Promo deactivated.' : 'Promo activated.');
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not update status');
    }
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-surface text-white flex">
      <Sidebar
        onLogout={() => {
          clearAdminToken();
          navigate('/login', { replace: true });
        }}
      />
      <main className="flex-1 p-6 overflow-y-auto">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-serif text-white">App promos</h1>
            <p className="text-xs text-muted mt-1">
              Create and manage in-app promo cards (image, link, optional screen key).
            </p>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => setCreateOpen(true)}
          >
            + Create promo
          </button>
        </header>

        {isLoading && (
          <div className="card text-center py-12 text-muted text-sm">Loading promos…</div>
        )}

        {error && (
          <div className="card border-rose/40 text-rose text-sm mb-4">
            {error.message || 'Failed to load promos.'}
            <button type="button" className="btn-ghost ml-3" onClick={() => reload()}>
              Retry
            </button>
          </div>
        )}

        {!isLoading && !error && promos.length === 0 && (
          <div className="card text-center py-12">
            <p className="text-muted text-sm mb-4">No promos yet.</p>
            <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
              Create your first promo
            </button>
          </div>
        )}

        {!isLoading && promos.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {promos.map((promo) => (
              <div key={promo.id} className="card overflow-hidden p-0">
                <div className="relative aspect-video bg-[#0a1219]">
                  {promo.image ? (
                    <img
                      src={absoluteImageUrl(promo.image)}
                      alt={promo.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted text-xs">
                      No image
                    </div>
                  )}
                  <span
                    className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      promo.isActive ? 'bg-emerald-600/90' : 'bg-zinc-600/90'
                    }`}
                  >
                    {promo.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="p-3 space-y-2">
                  <h3 className="text-sm font-semibold text-white line-clamp-2">{promo.title}</h3>
                  <a
                    href={promo.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-sage-light hover:underline break-all block"
                  >
                    {promo.link}
                  </a>
                  {promo.screen ? (
                    <p className="text-[10px] text-muted">Screen: {promo.screen}</p>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <label className="flex items-center gap-2 text-[11px] text-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!promo.isActive}
                        onChange={() => toggleActive(promo)}
                        className="rounded border-panel-border"
                      />
                      Active
                    </label>
                    <span className="flex-1" />
                    <button
                      type="button"
                      className="btn-ghost text-xs py-1 px-2"
                      onClick={() => openEdit(promo)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn-danger text-xs py-1 px-2"
                      onClick={() => {
                        setSelected(promo);
                        setDeleteOpen(true);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {createOpen && (
        <div
          className="modal-overlay"
          onClick={() => !busy && setCreateOpen(false)}
          role="presentation"
        >
          <div
            className="modal-card w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="promo-create-title"
          >
            <h2 id="promo-create-title" className="text-lg font-semibold mb-1">
              Create promo
            </h2>
            <p className="text-xs text-muted mb-4">Image max 5MB (JPEG, PNG, GIF, WebP).</p>
            <div className="space-y-3">
              <div>
                <label className="label" htmlFor="pc-title">
                  Title *
                </label>
                <input
                  id="pc-title"
                  className="input"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Summer sale"
                />
              </div>
              <div>
                <label className="label" htmlFor="pc-link">
                  Link *
                </label>
                <input
                  id="pc-link"
                  className="input"
                  value={form.link}
                  onChange={(e) => setForm((p) => ({ ...p, link: e.target.value }))}
                  placeholder="https://…"
                />
              </div>
              <div>
                <label className="label">Image *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onPickImage(e, false)}
                />
                <button
                  type="button"
                  className="btn-ghost w-full text-xs"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose image
                </button>
                {form.image && createPreviewUrl && (
                  <div className="mt-2 relative aspect-video rounded-lg overflow-hidden border border-panel-border">
                    <img
                      src={createPreviewUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      className="absolute top-2 right-2 btn-danger text-[10px] py-0.5 px-2"
                      onClick={() => {
                        setForm((p) => ({ ...p, image: null }));
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="label" htmlFor="pc-order">
                  Order
                </label>
                <input
                  id="pc-order"
                  type="number"
                  className="input"
                  value={form.order}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, order: parseInt(e.target.value, 10) || 0 }))
                  }
                />
              </div>
              <div>
                <label className="label" htmlFor="pc-screen">
                  Screen (optional)
                </label>
                <input
                  id="pc-screen"
                  className="input"
                  value={form.screen}
                  onChange={(e) => setForm((p) => ({ ...p, screen: e.target.value }))}
                  placeholder="e.g. promos"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" className="btn-ghost" disabled={busy} onClick={() => setCreateOpen(false)}>
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={busy} onClick={handleCreate}>
                {busy ? 'Saving…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editOpen && selected && (
        <div
          className="modal-overlay"
          onClick={() => !busy && setEditOpen(false)}
          role="presentation"
        >
          <div
            className="modal-card w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h2 className="text-lg font-semibold mb-4">Edit promo</h2>
            <div className="space-y-3">
              <div>
                <label className="label">Title *</label>
                <input
                  className="input"
                  value={editForm.title}
                  onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                />
              </div>
              <div>
                <label className="label">Link *</label>
                <input
                  className="input"
                  value={editForm.link}
                  onChange={(e) => setEditForm((p) => ({ ...p, link: e.target.value }))}
                />
              </div>
              <div>
                <label className="label">Image</label>
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onPickImage(e, true)}
                />
                <button
                  type="button"
                  className="btn-ghost w-full text-xs"
                  onClick={() => editFileInputRef.current?.click()}
                >
                  Change image
                </button>
                {(editForm.image || editForm.imageUrl) && (
                  <div className="mt-2 relative aspect-video rounded-lg overflow-hidden border border-panel-border">
                    <img
                      src={
                        editForm.image && editPreviewUrl
                          ? editPreviewUrl
                          : absoluteImageUrl(editForm.imageUrl)
                      }
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="label">Order</label>
                <input
                  type="number"
                  className="input"
                  value={editForm.order}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, order: parseInt(e.target.value, 10) || 0 }))
                  }
                />
              </div>
              <div>
                <label className="label">Screen (optional)</label>
                <input
                  className="input"
                  value={editForm.screen}
                  onChange={(e) => setEditForm((p) => ({ ...p, screen: e.target.value }))}
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                className="btn-ghost"
                disabled={busy}
                onClick={() => {
                  setEditOpen(false);
                  setSelected(null);
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={busy} onClick={handleUpdate}>
                {busy ? 'Saving…' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteOpen && selected && (
        <div
          className="modal-overlay"
          onClick={() => !busy && setDeleteOpen(false)}
          role="presentation"
        >
          <div
            className="modal-card w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h2 className="text-lg font-semibold mb-2">Delete promo</h2>
            <p className="text-sm text-muted mb-4">
              Delete <span className="text-white font-medium">{selected.title}</span>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" disabled={busy} onClick={() => setDeleteOpen(false)}>
                Cancel
              </button>
              <button type="button" className="btn-danger" disabled={busy} onClick={handleDelete}>
                {busy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
